import React, { useEffect } from "react";
import {
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import {
  LuArrowUpWideNarrow,
  LuArrowDownWideNarrow,
  LuArrowUpDown,
} from "react-icons/lu";
import Spinner from "components/spinner";
import ColumnDropdown from "./ColumnDropdown";

export default function WithoutPageTable({
  columns,
  data = [],
  loading,
  error,
  isColumnDropdown = false,
  initialColumnVisibility,
  tableTitle,
  actionButton,
}) {
  const [sorting, setSorting] = React.useState([]);
  const [columnVisibility, setColumnVisibility] = React.useState(
    initialColumnVisibility || {}
  );
  const table = useReactTable({
    data,
    columns,
    state: { sorting, columnVisibility },
    onSortingChange: setSorting,
    onColumnVisibilityChange: setColumnVisibility,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  useEffect(() => {
    setColumnVisibility(initialColumnVisibility);
  }, [initialColumnVisibility]);

  return (
    <div className="relativ min-h-[30vh]">
      <header className="relative flex items-center justify-between pt-4">
        <div className="text-xl font-bold text-navy-700 dark:text-white">
          {tableTitle}
        </div>
        <div className="flex flex-col gap-2 md:flex-row">
          {isColumnDropdown && <ColumnDropdown table={table} />}
          {actionButton}
        </div>
      </header>
      <div className="custom-scrollbar mt-4  overflow-x-auto rounded-md border border-gray-200 dark:border-white/10">
        <table className="w-full border-collapse">
          <thead className="bg-gray-50 dark:bg-white/5">
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header, index) => {
                  const isSorted = header.column.getIsSorted();
                  const canSort = header.column.getCanSort?.() ?? true;

                  return (
                    <th
                      key={header.id}
                      colSpan={header.colSpan}
                      onClick={
                        canSort
                          ? header.column.getToggleSortingHandler()
                          : undefined
                      }
                      className={[
                        "border-b   px-4 pb-2 pt-4 text-start dark:border-white/10",
                        canSort ? "cursor-pointer select-none" : "",
                        // index === 0 ? "rounded-tl-md" : "",
                        index === headerGroup.headers.length - 1
                          ? " "
                          : "border-r",
                      ].join(" ")}
                    >
                      <div className="flex items-center justify-start text-xs font-bold text-gray-600 dark:text-white">
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                        <span
                          className={`ml-2 text-base ${
                            canSort
                              ? "opacity-100"
                              : "pointer-events-none opacity-0"
                          }`}
                          aria-hidden={!isSorted}
                        >
                          {isSorted === "asc" ? (
                            <LuArrowUpWideNarrow />
                          ) : isSorted === "desc" ? (
                            <LuArrowDownWideNarrow />
                          ) : (
                            <LuArrowUpDown />
                          )}
                        </span>
                      </div>
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>

          <tbody>
            {loading && (
              <tr className="border-b border-gray-200 hover:bg-gray-50 dark:border-white/[0.1] dark:hover:bg-white/[0.05]">
                <td colSpan={table.getAllColumns().length} className="py-2.5">
                  <div className="flex w-full items-center justify-center">
                    <Spinner />
                  </div>
                </td>
              </tr>
            )}
            {error && (
              <tr className="border-b border-gray-200 dark:border-white/[0.1]">
                <td
                  colSpan={table.getAllColumns().length}
                  className="px-6 py-4"
                >
                  <div className="flex items-center justify-center">
                    <div className="flex items-center gap-2 rounded-md border border-red-200 bg-red-50 px-4 py-2 text-sm font-medium text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-4 w-4 shrink-0"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={2}
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M12 9v2m0 4h.01M12 3C7.03 3 3 7.03 3 12s4.03 9 9 9 9-4.03 9-9-4.03-9-9-9z"
                        />
                      </svg>
                      <span>{error}</span>
                    </div>
                  </div>
                </td>
              </tr>
            )}
            {table.getRowModel().rows.length > 0
              ? table.getRowModel().rows.map((row, rowIndex) => (
                  <tr
                    key={row.id}
                    className={[
                      "cursor-pointer whitespace-nowrap hover:bg-gray-50 dark:hover:bg-white/5 ",
                      rowIndex === table.getRowModel().rows.length - 1
                        ? "rounded-b-md"
                        : "",
                    ].join(" ")}
                  >
                    {row.getVisibleCells().map((cell, cellIndex) => (
                      <td
                        key={cell.id}
                        className={[
                          "border-b border-gray-200 px-4 py-3 text-sm font-medium text-navy-700 dark:border-white/10 dark:text-white",
                          cellIndex !== row.getVisibleCells().length - 1
                            ? "border-r border-gray-200 dark:border-white/10"
                            : "",
                        ].join(" ")}
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </td>
                    ))}
                  </tr>
                ))
              : !loading && (
                  <tr>
                    <td
                      colSpan={table.getAllColumns().length}
                      className="py-6 text-center text-gray-500 dark:text-gray-400"
                    >
                      No records found
                    </td>
                  </tr>
                )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
